import React, { useEffect, useRef, useState } from "react";
import { Music, VolumeX, ChevronDown } from "lucide-react";
import { PinStar } from "@/components/Favorites";

/**
 * MusicPicker — compact dropdown playlist for a character page.
 *
 * props:
 *   playlist: Array<{ id, url, label }>
 *   accent: hex color
 *   testId: data-testid prefix
 *   defaultTrackId: optional initial track id
 *   persona: label shown under track in Favorites mini-player (e.g. "Vate")
 */
export default function MusicPicker({ playlist = [], accent = "#f5d572", testId = "music-picker", defaultTrackId, persona }) {
  const [open, setOpen] = useState(false);
  const [trackId, setTrackId] = useState(() => defaultTrackId || playlist[0]?.id || "");
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const current = playlist.find((t) => t.id === trackId) || playlist[0] || null;

  // Silence option = synthetic "none" id → stops playback
  const isSilence = trackId === "silence";

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (!playing || isSilence || !current) {
      try { el.pause(); } catch { /* ignore */ }
      return;
    }
    el.volume = 0.3;
    el.loop = true;
    el.load();
    el.play().catch(() => setPlaying(false));
  }, [trackId, playing, isSilence, current]);

  const toggle = () => {
    if (isSilence) { setPlaying(false); return; }
    setPlaying((p) => !p);
  };

  const pick = (id) => {
    setTrackId(id);
    setOpen(false);
    if (id === "silence") setPlaying(false);
    else setPlaying(true);
  };

  if (!playlist.length) return null;

  return (
    <div className="relative" data-testid={`${testId}-root`}>
      <audio ref={audioRef} src={isSilence ? "" : (current?.url || "")} preload="none" crossOrigin="anonymous" />
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={toggle}
          data-testid={`${testId}-toggle`}
          className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-2 rounded-l-full"
          style={{
            background: playing ? `${accent}22` : "rgba(10,10,16,0.55)",
            border: `1px solid ${accent}66`,
            borderRight: "none",
            color: playing ? accent : "rgba(240,230,200,0.75)",
          }}>
          {playing && !isSilence ? <VolumeX size={13} /> : <Music size={13} />}
          <span className="hidden sm:inline">{isSilence ? "Silence" : (current?.label || "—")}</span>
          <span className="sm:hidden">{playing && !isSilence ? "on" : "off"}</span>
        </button>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={`${testId}-dropdown`}
          className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-2 rounded-r-full"
          style={{
            background: open ? `${accent}22` : "rgba(10,10,16,0.55)",
            border: `1px solid ${accent}66`,
            color: open ? accent : "rgba(240,230,200,0.75)",
          }}>
          <ChevronDown size={13} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 180ms ease" }} />
        </button>
      </div>

      {open && (
        <div
          className="absolute right-0 mt-2 min-w-[220px] rounded-xl overflow-hidden z-50"
          style={{
            background: "rgba(10,10,16,0.98)",
            border: `1px solid ${accent}55`,
            boxShadow: `0 18px 42px -16px ${accent}30`,
          }}
          data-testid={`${testId}-menu`}>
          {playlist.map((t) => (
            <div
              key={t.id}
              data-testid={`${testId}-row-${t.id}`}
              style={{
                display: "flex",
                alignItems: "center",
                background: t.id === trackId ? `${accent}25` : "transparent",
                borderBottom: "1px solid rgba(255,255,255,0.04)",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = `${accent}18`}
              onMouseLeave={(e) => e.currentTarget.style.background = t.id === trackId ? `${accent}25` : "transparent"}
            >
              <button
                type="button"
                onClick={() => pick(t.id)}
                data-testid={`${testId}-option-${t.id}`}
                className="flex-1 text-left px-3 py-2.5 text-[12px] font-mono transition-colors"
                style={{
                  background: "transparent",
                  color: t.id === trackId ? accent : "rgba(240,230,200,0.85)",
                }}
              >
                {t.label}
              </button>
              <div className="pr-2">
                <PinStar track={{ id: t.id, url: t.url, label: t.label, persona }} />
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => pick("silence")}
            data-testid={`${testId}-option-silence`}
            className="w-full text-left px-3 py-2.5 text-[12px] font-mono"
            style={{
              background: trackId === "silence" ? `${accent}25` : "transparent",
              color: trackId === "silence" ? accent : "rgba(240,230,200,0.6)",
              fontStyle: "italic",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = `${accent}18`}
            onMouseLeave={(e) => e.currentTarget.style.background = trackId === "silence" ? `${accent}25` : "transparent"}>
            — Silence pur —
          </button>
        </div>
      )}
    </div>
  );
}
